import React from 'react'
import { OffertaType } from '@next-nx/utils/types'
import { useTrans } from '@next-nx/hooks'
import { numberNotation, priceNotation } from '@next-nx/utils/safe'
import { Bin } from '@next-nx/shared-ui/icons'
import { Button, OffertaCardTag, WImage } from '@next-nx/shared-ui/atoms'
import styles from './ComparatoreCardFull.module.sass'
import { getCategoriaUrlParam } from '@next-nx/utils'
import { useRouter } from 'next/router'


interface Props {
  offerta: OffertaType
  showIi?: boolean
  showIe?: boolean
  onRemove?: () => void
  className?: string
  handleTracking?: (type: string, effect: string, cta: string, product?: string) => void
}

const ComparatoreCardFull = (props: Props) => {

  const { 
    offerta = undefined,
    showIi = false,
    showIe = false,
    onRemove = undefined,
    className = '',
    handleTracking = () => undefined
  } = props

  const t = useTrans()
  const router = useRouter()

  return offerta ? (
    <div className={`${styles.root} ${className}`}>
      <div className={styles.card}>
        {(offerta.prontaConsegna || offerta.gammaEcologica) && (
          <div className={styles.tags}>
            {offerta.prontaConsegna && (<OffertaCardTag label={t("Pronta consegna")} color="blue" />)}
            {offerta.gammaEcologica && (<OffertaCardTag label={t("Gamma ecologica")} color="green" />)}
            {offerta.tuttoIncluso && (<OffertaCardTag label={t("Tutto incluso")} color="purple" />)}
          </div>
        )}
        <div className={styles.imageBox}>
          <WImage 
            src={offerta.veicolo?.mainImmagineStatica || ""}
            alt={offerta.nome}
            title={offerta.nome}
            maxWidth={227}
            maxHeight={163}
            layout="fill"
            objectFit="contain"
            objectPosition="50% 50%"
          />
        </div>
        <div className={styles.content}>
          <p className={styles.title}>{offerta.nome}</p>
          {(!!offerta.condizioniMesi || !!offerta.condizioniKm) && (
            <p className={styles.condizioni}>
              {!!offerta.condizioniMesi && <span>{offerta.condizioniMesi} {t("mesi")}</span>}
              {!!offerta.condizioniKm && <span>{numberNotation(offerta.condizioniKm)} {t("km/anno")}</span>}
            </p>
          )}

          {/* Anticipo */}
          {offerta.anticipoZero ? (
            <p className={`${styles.anticipo} ${styles['anticipo--senza']}`}>
              {t("Senza anticipo")}
            </p>
          ) : showIi && showIe ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIi && !!offerta.anticipoIe ? (
                <>
                  <span className={styles.anticipoTitle}>{t("Anticipo:")}</span>{' '}
                  <span className={styles.anticipoIi}>
                    <span className={styles.anticipoValue}>{priceNotation(offerta.anticipoIi)}</span>{' '}
                    <span className={styles.ivaText}>{t("i.i.")}</span>
                  </span>{' '}-{' '}
                  <span className={styles.anticipoIe}>
                    <span className={styles.anticipoValue}>{priceNotation(offerta.anticipoIe)}</span>{' '}
                    <span className={styles.ivaText}>{t("i.e.")}</span>
                  </span>
                </>
              ) : null}
            </p>
          ) : showIi ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIi && (
                <>
                  <span className={styles.anticipoTitle}>{t("Anticipo:")}</span>
                  <span className={styles.anticipoIi}>
                    <span className={styles.anticipoValue}>{priceNotation(offerta.anticipoIi)}</span>{' '}
                    <span className={styles.ivaText}>{t("iva inclusa")}</span>
                  </span> 
                </>
              )}
            </p>
          ) : showIe ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIe && (
                <>
                  <span className={styles.anticipoTitle}>{t("Anticipo:")}</span>
                  <span className={styles.anticipoIe}>
                    <span className={styles.anticipoValue}>{priceNotation(offerta.anticipoIe)}</span>{' '}
                    <span className={styles.ivaText}>{t("iva esclusa")}</span>
                  </span> 
                </>
              )}
            </p>
          ) : null}

          {/* Prezzi */}
          {!offerta.prezzoIi && !offerta.prezzoIe ? (
            <></>
          ) : showIi && showIe ? (
            <div className={`${styles.prices} ${styles['prices--double']}`}>
              {!!offerta.prezzoIi && (
                <div className={styles.price}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIi)}</span>
                  <span className={styles.priceLabel}>{t("iva inclusa")}</span>
                </div>
              )}
              {!!offerta.prezzoIe && (
                <div className={`${styles.price} ${styles['price--ie']}`}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIe)}</span>
                  <span className={styles.priceLabel}>{t("iva esclusa")}</span>
                </div>
              )}
            </div>
          ) : showIi ? (
            <div className={`${styles.prices}`}>
              {!!offerta.prezzoIi && (
                <div className={styles.price}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIi)}</span>
                  <span className={styles.priceLabel}>{t("iva inclusa")}</span>
                </div>
              )}
            </div>
          ) : showIe ? (
            <div className={`${styles.prices}`}>
              {!!offerta.prezzoIe && (
                <div className={`${styles.price} ${styles['price--ie']}`}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIe)}</span>
                  <span className={styles.priceLabel}>{t("iva esclusa")}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.actions}>
        <Button label={t("Configura veicolo")} labelMobile={t("Configura")} href={offerta.url + getCategoriaUrlParam(router?.query?.categoria)} handleTracking={() => handleTracking('configura_veicolo', 'configurazione_veicolo_comparatore', 'configura_veicolo_comparatore', offerta?.nome)} />
        <Button label={t("Rimuovi")} variant="ghost" icon={<Bin />} onClick={onRemove} handleTracking={() => handleTracking('rimuovi', 'rimozione_veicolo_comparatore', 'rimuovi_comparatore', offerta?.nome)} />
      </div>
    </div>
  ) : <></>
}

export default ComparatoreCardFull
